import { useState, useEffect, useCallback } from 'react'
import { legalAPI } from '@/utils/api'
import {
  type CasePhase,
  type MilestoneWithStatus,
  type NextStep,
  type CompactMilestone,
  inferCasePhase,
  deriveMilestoneStatuses,
  getNextSteps,
  phaseConfig,
  phaseOrder,
  compactMilestones,
} from './constants'

// ============================================================================
// Types
// ============================================================================

interface TimelineEvent {
  id: number
  event_type: 'action' | 'correspondence'
  action_type?: string
  direction?: string
  title: string
  description?: string
  action_date: string
  deadline?: string
  visible_to_client: boolean
  party_type?: string
  party_name?: string
  created_at: string
}

interface LegalProcess {
  id: number
  rsv_company?: number
  rsv_company_name?: string
  rsv_status: string
  rsv_policy_number?: string
  rsv_reference?: string
  mahnschreiben_sent_at?: string
  klage_filed_at?: string
  next_hearing_date?: string
  next_deadline?: string
  court_name?: string
  court_case_number?: string
}

interface DeadlineInfo {
  label: string
  description?: string
  deadline: string
  daysUntil: number
  urgency: 'normal' | 'warning' | 'urgent'
}

export interface CompactMilestoneStatus {
  id: string
  label: string
  shortLabel: string
  status: 'completed' | 'current' | 'pending' | 'skipped'
}

export interface CaseStatusData {
  phase: CasePhase
  phaseConfig: typeof phaseConfig[CasePhase]
  phaseSince?: string
  milestones: MilestoneWithStatus[]
  compactMilestones: CompactMilestoneStatus[]
  nextSteps: NextStep[]
  deadline?: DeadlineInfo
  actionTypes: Set<string>
  legalProcess?: LegalProcess
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// ============================================================================
// Helper Functions
// ============================================================================

function findPhaseSince(
  phase: CasePhase,
  actions: Array<{ action_type: string; action_date: string }>
): string | undefined {
  // Find the action that triggered entry into this phase
  const phaseActionMap: Record<CasePhase, string[]> = {
    closed: ['case_closed'],
    resolution: ['case_won', 'case_lost', 'settlement_reached', 'payment_received'],
    litigation: ['klage_filed'],
    pre_litigation: ['mahnschreiben_sent'],
    rsv: ['rsv_request_sent'],
    intake: [],
  }

  const triggers = phaseActionMap[phase]
  if (triggers.length === 0) {
    // Intake phase - return earliest action or undefined
    return actions.length > 0 ? actions[actions.length - 1]?.action_date : undefined
  }

  // Find the first triggering action for this phase
  const triggeringAction = [...actions]
    .reverse()
    .find((a) => triggers.includes(a.action_type))

  return triggeringAction?.action_date
}

function deriveDeadline(legalProcess?: LegalProcess): DeadlineInfo | undefined {
  // Check for upcoming deadline from LegalProcess
  if (legalProcess?.next_deadline) {
    const deadline = new Date(legalProcess.next_deadline)
    const now = new Date()
    const daysUntil = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (daysUntil >= 0) {
      return {
        label: 'Frist',
        deadline: legalProcess.next_deadline,
        daysUntil,
        urgency: daysUntil <= 3 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
      }
    }
  }

  // Check for upcoming hearing
  if (legalProcess?.next_hearing_date) {
    const hearingDate = new Date(legalProcess.next_hearing_date)
    const now = new Date()
    const daysUntil = Math.ceil((hearingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (daysUntil >= 0) {
      return {
        label: 'Gerichtstermin',
        description: legalProcess.court_name,
        deadline: legalProcess.next_hearing_date,
        daysUntil,
        urgency: daysUntil <= 3 ? 'urgent' : daysUntil <= 7 ? 'warning' : 'normal',
      }
    }
  }

  return undefined
}

function deriveCompactMilestones(
  phase: CasePhase,
  actionTypes: Set<string>,
  hasRsvRequest: boolean
): CompactMilestoneStatus[] {
  const currentPhaseIndex = phaseOrder.indexOf(phase)

  return compactMilestones.map((m) => {
    let status: 'completed' | 'current' | 'pending' | 'skipped'

    // Map compact milestone ID to relevant actions
    const milestoneActions: Record<string, string[]> = {
      rsv: ['rsv_approved', 'rsv_denied', 'rsv_partial'],
      mahnschreiben: ['mahnschreiben_sent'],
      klage: ['klage_filed'],
      resolution: ['case_won', 'case_lost', 'settlement_reached', 'payment_received', 'case_closed'],
    }

    const milestonePhases: Record<string, CasePhase> = {
      rsv: 'rsv',
      mahnschreiben: 'pre_litigation',
      klage: 'litigation',
      resolution: 'resolution',
    }

    const triggers = milestoneActions[m.id] || []
    const milestonePhase = milestonePhases[m.id]
    const milestonePhaseIndex = phaseOrder.indexOf(milestonePhase)

    // Check if completed
    if (triggers.some((t) => actionTypes.has(t))) {
      status = 'completed'
    }
    // Special case: RSV skipped if no request and we're past RSV phase
    else if (m.id === 'rsv' && !hasRsvRequest && currentPhaseIndex > phaseOrder.indexOf('rsv')) {
      status = 'skipped'
    }
    // Check if this is the current phase
    else if (milestonePhaseIndex === currentPhaseIndex) {
      status = 'current'
    }
    // Check if skipped (past phase but not completed)
    else if (milestonePhaseIndex < currentPhaseIndex) {
      status = 'skipped'
    }
    // Future
    else {
      status = 'pending'
    }

    return {
      ...m,
      status,
    }
  })
}

// ============================================================================
// Main Hook
// ============================================================================

export function useCaseStatus(caseId: number): CaseStatusData {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [legalProcess, setLegalProcess] = useState<LegalProcess | undefined>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch timeline and legal process in parallel
      const [timelineResponse, processResponse] = await Promise.all([
        legalAPI.getTimeline(caseId),
        legalAPI.getLegalProcess(caseId).catch(() => null), // Legal process may not exist
      ])

      setTimeline(timelineResponse.timeline || [])
      setLegalProcess(processResponse?.process || undefined)
    } catch (err) {
      console.error('Failed to fetch case status:', err)
      setError('Status konnte nicht geladen werden')
    } finally {
      setIsLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Extract action types from timeline
  const actions = timeline
    .filter((e) => e.event_type === 'action' && e.action_type)
    .map((e) => ({
      action_type: e.action_type!,
      action_date: e.action_date,
      title: e.title,
    }))

  const actionTypes = new Set(actions.map((a) => a.action_type))

  // Infer current phase
  const phase = inferCasePhase(actionTypes)

  // Check if RSV request was sent (for milestone skipping logic)
  const hasRsvRequest = actionTypes.has('rsv_request_sent')

  // Derive milestone statuses (full version for detailed view)
  const milestones = deriveMilestoneStatuses(actionTypes, actions, phase, hasRsvRequest)

  // Derive compact milestones (for horizontal progress display)
  const compactMilestoneStatuses = deriveCompactMilestones(phase, actionTypes, hasRsvRequest)

  // Find when current phase started
  const phaseSince = findPhaseSince(phase, actions)

  // Derive deadline info
  const deadline = deriveDeadline(legalProcess)

  // Get contextual next steps
  const nextSteps = getNextSteps(phase, actionTypes)

  return {
    phase,
    phaseConfig: phaseConfig[phase],
    phaseSince,
    milestones,
    compactMilestones: compactMilestoneStatuses,
    nextSteps,
    deadline,
    actionTypes,
    legalProcess,
    isLoading,
    error,
    refetch: fetchData,
  }
}
